import type { Component } from '../../router';
import { HealthPill } from '../HealthPill';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { icon } from '../icons';
import { createCartButton, type MountedCartButton } from './CartButton';
import { isActiveTab, topNavItems, tabBarItems } from './navModel';

// AppNav — the two navigation primitives, formalized out of AppShell so
// each is an independently mountable, testable layout component. Both read the
// shared navModel and filter by the RBAC mirror; which one is visible is
// pure CSS (nav.css flips at 768px), so the shell mounts both and lets the
// viewport choose.

// TopNav — the desktop top navigation: brand, the office menu, and the health
// pill. Hidden below 768px (nav.css).
export class TopNav implements Component {
  private root: HTMLElement | null = null;
  private pill: HealthPill | null = null;
  private lang: LanguageSwitcher | null = null;
  private cart: MountedCartButton | null = null;

  mount(host: HTMLElement): void {
    const nav = document.createElement('header');
    nav.className = 'topnav';

    const inner = document.createElement('div');
    inner.className = 'topnav__inner';

    const brand = document.createElement('a');
    brand.className = 'topnav__brand';
    brand.href = '/';
    brand.textContent = 'Asmaluz';

    const links = document.createElement('nav');
    links.className = 'topnav__links';
    for (const { href, label } of topNavItems()) {
      const a = document.createElement('a');
      a.className = 'topnav__link';
      a.href = href;
      a.textContent = label;
      // Top-nav links light only on their own page (exact match); the tab bar
      // uses prefix matching so a parent tab stays lit on sub-pages.
      if (location.pathname === href) a.setAttribute('aria-current', 'page');
      links.appendChild(a);
    }

    this.pill = new HealthPill();
    this.pill.mount(links);

    this.lang = new LanguageSwitcher();
    this.lang.mount(links);

    this.cart = createCartButton('top');
    links.appendChild(this.cart.el);

    inner.append(brand, links);
    nav.appendChild(inner);
    host.appendChild(nav);
    this.root = nav;
  }

  unmount(): void {
    this.pill?.unmount();
    this.pill = null;
    this.lang?.unmount();
    this.lang = null;
    this.cart?.destroy();
    this.cart = null;
    this.root?.remove();
    this.root = null;
  }
}

// TabBar — the phone bottom navigation: the four thumb-reachable field
// destinations as icon + label tabs. Hidden at 768px and up (nav.css).
export class TabBar implements Component {
  private root: HTMLElement | null = null;
  private cart: MountedCartButton | null = null;

  mount(host: HTMLElement): void {
    const bar = document.createElement('nav');
    bar.className = 'tabbar';
    bar.setAttribute('aria-label', 'Primary');

    for (const item of tabBarItems()) {
      const a = document.createElement('a');
      a.className = 'tabbar__item';
      a.href = item.href;
      if (isActiveTab(item.href)) a.setAttribute('aria-current', 'page');

      const iconBox = document.createElement('span');
      iconBox.className = 'tabbar__icon';
      if (item.icon) iconBox.appendChild(icon(item.icon));

      const label = document.createElement('span');
      label.textContent = item.tabLabel ?? item.label;

      a.append(iconBox, label);
      bar.appendChild(a);
    }

    // The cart is a tab-styled button (opens the drawer) rather than a nav link.
    this.cart = createCartButton('tab');
    bar.appendChild(this.cart.el);

    host.appendChild(bar);
    this.root = bar;
  }

  unmount(): void {
    this.cart?.destroy();
    this.cart = null;
    this.root?.remove();
    this.root = null;
  }
}
